import React, { useState } from 'react';
import { AppBar, Drawer, Toolbar, IconButton, Box } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import clsx from 'clsx';
import { Link, useHistory } from 'react-router-dom';
import { UpliftLogo } from '@uplift/icons';
import { routeIsSession, ROUTING_CONFIG } from '@uplift/constants';
import { LightLayoutType } from '@uplift/types';

import { ScrollableView } from '../components/ScrollableView.component';
import { Loader } from '../../Loader';
import { ProfileMenu } from '../components/ProfileMenu';
import { LightLayoutContextProvider } from './light-layout-context';
import { FlexSpacer } from '../components/FlexSpacer';
import { useDrawerStyles } from './right-drawer.styles';
import { useLayoutClasses } from '@uplift/services';


const ConditionalUpliftLink: React.VFC<{ pathname: string }> = ({ pathname }) => {
  if (routeIsSession(pathname)) {
    return (<UpliftLogo />);
  }

  return (
    <Link to={ROUTING_CONFIG.home} style={{ display: 'flex' }}>
      <UpliftLogo />
    </Link>
  );
};


export type LightLayoutProps = LightLayoutType;


export const LightLayoutProvider: React.FC<LightLayoutProps> = ({
  children,
  onBack,
  leftActions,
  classes: _classes,
  loading,
  showAvatar = true,
  showToolbar = true,
}) => {
  const history = useHistory();
  const { classes } = useLayoutClasses({ drawerWidth: undefined, classes: _classes });
  const { classes: drawerClasses } = useDrawerStyles();
  const [ showDrawer, setShowDrawer ] = useState(false);
  const [ drawerContent, setDrawerContent ] = useState<React.ReactNode>(null);
  const body = loading ? <Loader color="primary" height="37em" /> : children;
  const ctx = React.useMemo(() => ({
    showDrawer,
    setShowDrawer,
    drawerContent,
    setDrawerContent
  }), [ showDrawer, drawerContent ]);

  return (
    <div className={classes.root}>
      {showToolbar && (
        <AppBar position="fixed" className={clsx(classes.appBar, showDrawer && drawerClasses.appBarShift)}>
          <Toolbar className={classes.toolBar}>
            {onBack && (
              <IconButton onClick={onBack} size="small" className={drawerClasses.backButton}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <ConditionalUpliftLink pathname={history?.location?.pathname} />

            {leftActions}

            <FlexSpacer />

            <Box textAlign="right">
              <ProfileMenu showAvatar={showAvatar} />
            </Box>
          </Toolbar>
        </AppBar>
      )}
      <Box display="flex" flexDirection="column" flex="1" width="100%">
        {showToolbar && <Toolbar />}
        <Box display="flex" flex="1">
          <ScrollableView>
            <LightLayoutContextProvider {...ctx}>
              <main className={drawerClasses.content} id="light-layout">
                {body}
              </main>
            </LightLayoutContextProvider>
          </ScrollableView>
          <Drawer
            anchor="right"
            variant="persistent"
            open={showDrawer}
            className={clsx(drawerClasses.drawer, {
              [drawerClasses.drawerOpened]: showDrawer
            })}
            classes={{
              paper: drawerClasses.paper,
            }}
          >
            <Toolbar />
            {drawerContent}
          </Drawer>
        </Box>
      </Box>
    </div>
  );
};
